import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { QSO } from "@/types/qso.types";
import { QSOADIFImportService } from "@/services/adif/qso-adif-import.service";
import { qsoRepository } from "@/db/repositories/qso.repository";

interface QSOState {
  qsos: QSO[];
  loading: boolean;
  error: string | null;
  importProgress: number;
  duplicateCount: number;
}

const initialState: QSOState = {
  qsos: [],
  loading: false,
  error: null,
  importProgress: 0,
  duplicateCount: 0,
};

// 加载所有QSO记录
export const loadQSORecords = createAsyncThunk(
  "qsos/loadQSORecords",
  async (_, { rejectWithValue }) => {
    try {
      const qsos = await qsoRepository.findAll();
      return qsos;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : "加载QSO记录失败");
    }
  }
);

export const importQSOs = createAsyncThunk(
  "qsos/importQSOs",
  async (file: File, { rejectWithValue }) => {

    
    try {
      const service = new QSOADIFImportService();
      const result = await service.importADIF(file);

      
      // 导入完成后，加载所有QSO记录以确保数据同步
      const allQsos = await qsoRepository.findAll();

      
      const finalResult = { ...result, allQsos };

      
      return finalResult;
    } catch (error) {

      return rejectWithValue(error instanceof Error ? error.message : "未知错误");
    }
  }
);

// 清空QSO记录
export const clearQSORecords = createAsyncThunk(
  "qsos/clearQSORecords",
  async (_, { rejectWithValue }) => {

    
    try {
      // 清空数据库中的QSO记录
      await qsoRepository.deleteAll();

      
      return [];
    } catch (error) {

      return rejectWithValue(error instanceof Error ? error.message : "清空QSO记录失败");
    }
  }
);

const qsoSlice = createSlice({
  name: "qsos",
  initialState,
  reducers: {
    setImportProgress: (state, action: PayloadAction<number>) => {
      state.importProgress = action.payload;
    },
    // 保留原有的clearQSOs action用于清空Redux store（主要用于UI更新）
    clearQSOs: (state) => {
      state.qsos = [];
      state.duplicateCount = 0;
    },
  },
  extraReducers: (builder) => {
    builder
      // 加载QSO记录的处理
      .addCase(loadQSORecords.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadQSORecords.fulfilled, (state, action) => {
        state.loading = false;
        state.qsos = action.payload;
      })
      .addCase(loadQSORecords.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // 导入QSO的处理
      .addCase(importQSOs.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.importProgress = 0;
      })
      .addCase(importQSOs.fulfilled, (state, action) => {
        state.loading = false;
        state.duplicateCount = action.payload.duplicateCount;
        state.importProgress = 100;
        state.qsos = action.payload.allQsos;
      })
      .addCase(importQSOs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // 清空QSO记录的处理
      .addCase(clearQSORecords.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(clearQSORecords.fulfilled, (state, action) => {
        state.loading = false;
        state.qsos = action.payload;
        state.duplicateCount = 0;
      })
      .addCase(clearQSORecords.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setImportProgress, clearQSOs } = qsoSlice.actions;
export default qsoSlice.reducer;
